import { useEffect, useRef } from "react";
import { Button } from "@nextui-org/react";
import { AnimatePresence, motion } from "framer-motion";
import { useProjectFocus } from "@/stores/projectFocusStore";
import { ARCH_CONTENT } from "../arch/archContent";
import FocusContentBlocks from "./FocusContentBlocks";

const SECTION_LABELS: Record<string, string> = {
  project: "Project",
  arch: "Architecture",
  varch: "Virtual Architecture",
  product: "Product Design",
};

// Full-height frosted side panel for the focused model, mounted outside the
// Canvas. Desktop: left panel with a feathered blur edge; mobile: bottom
// sheet. Fades in once the camera settles, cross-fades between models.
export default function ProjectFocusCard() {
  const focused = useProjectFocus((s) => s.focused);
  const settled = useProjectFocus((s) => s.settled);
  const clearFocus = useProjectFocus((s) => s.clearFocus);

  // Keep the last project around so the exit animation still has content
  // after `focused` flips to null.
  const lastFocused = useRef(focused);
  if (focused) lastFocused.current = focused;
  const project = focused ?? lastFocused.current;

  useEffect(() => {
    if (!focused) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const el = e.target as HTMLElement | null;
      if (
        el &&
        (el.tagName === "INPUT" ||
          el.tagName === "TEXTAREA" ||
          el.isContentEditable)
      ) {
        return;
      }
      // An open NextUI modal (arch drawings, product preview) owns ESC:
      // it closes itself and the focus view stays.
      if (document.querySelector('[role="dialog"]')) return;
      clearFocus();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [focused, clearFocus]);

  const visible = Boolean(focused && settled && project);
  const section = project
    ? (SECTION_LABELS[project.id.split(":")[0]] ?? "Project")
    : "";
  const blocks = project ? ARCH_CONTENT[project.id] : undefined;

  return (
    <AnimatePresence>
      {visible && project && (
        <motion.div
          data-focus-card=""
          key={project.id}
          initial={{ opacity: 0, x: -48 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -48 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="pointer-events-auto absolute inset-y-0 left-0 z-40 flex w-[calc(25vw+9rem)] min-w-[29rem] max-w-[95vw] flex-col justify-between bg-white/5 py-10 pl-10 pr-[6.5rem] backdrop-blur-2xl [mask-image:linear-gradient(to_right,black_calc(100%-4rem),transparent)] max-sm:inset-x-0 max-sm:bottom-0 max-sm:top-auto max-sm:h-[calc(46vh+6rem)] max-sm:w-full max-sm:min-w-0 max-sm:max-w-none max-sm:justify-end max-sm:gap-6 max-sm:px-6 max-sm:py-8 max-sm:[mask-image:linear-gradient(to_top,black_calc(100%-6rem),transparent)]"
        >
          {blocks ? (
            <>
              <div className="shrink-0">
                <p className="mt-12 text-xs uppercase tracking-[0.35em] text-white/50 max-sm:mt-0">
                  {section}
                </p>
                <h2 className="mt-4 text-3xl font-bold uppercase leading-[1.05] tracking-tight text-white max-sm:text-2xl">
                  {project.name}
                </h2>
              </div>

              <div className="mt-6 min-h-0 flex-1 overflow-y-auto overscroll-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [mask-image:linear-gradient(to_bottom,transparent,black_2rem,black_calc(100%-4rem),transparent)]">
                <FocusContentBlocks blocks={blocks} />
              </div>

              <div className="shrink-0 pt-2">
                <div className="flex items-center gap-3">
                  {project.onAction && project.actionLabel && (
                    <Button
                      radius="full"
                      variant="bordered"
                      className="border-white/40 text-white"
                      onPress={() => project.onAction?.()}
                    >
                      {project.actionLabel}
                    </Button>
                  )}
                  <Button
                    radius="full"
                    variant="light"
                    className="text-white/60 sm:hidden"
                    onPress={clearFocus}
                  >
                    Close
                  </Button>
                  <Button
                    isIconOnly
                    radius="full"
                    variant="flat"
                    aria-label="Close"
                    className="ml-auto bg-white/10 text-white/80 backdrop-blur-md max-sm:hidden"
                    onPress={clearFocus}
                  >
                    ✕
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="mt-12 max-sm:hidden">
                <Button
                  isIconOnly
                  radius="full"
                  variant="flat"
                  aria-label="Close"
                  className="bg-white/10 text-white/80 backdrop-blur-md"
                  onPress={clearFocus}
                >
                  ✕
                </Button>
              </div>

              <div>
                <p className="text-xs uppercase tracking-[0.35em] text-white/50">
                  {section}
                </p>
                {project.description && (
                  <p className="mt-5 max-w-[25rem] text-lg leading-relaxed text-white/85 max-sm:mt-3 max-sm:max-w-none max-sm:text-base">
                    {project.description}
                  </p>
                )}
              </div>

              <div>
                <div className="border-t border-dotted border-white/30" />
                <h2 className="mt-6 text-5xl font-bold uppercase leading-[1.05] tracking-tight text-white max-sm:mt-4 max-sm:text-3xl">
                  {project.name}
                </h2>
                <div className="mt-7 flex items-center gap-3 max-sm:mt-4">
                  {project.onAction && project.actionLabel && (
                    <Button
                      radius="full"
                      variant="bordered"
                      className="border-white/40 text-white"
                      onPress={() => project.onAction?.()}
                    >
                      {project.actionLabel}
                    </Button>
                  )}
                  <Button
                    radius="full"
                    variant="light"
                    className="text-white/60 sm:hidden"
                    onPress={clearFocus}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
